import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Calendar, Clock, ChevronRight, Star, BookOpen } from 'lucide-react';
import { Button } from './ui/button';

const WeeklyBriefPreview = () => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchFeaturedBrief();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeaturedBrief = async () => {
    try {
      const response = await axios.get(`${API}/macro-briefs/featured`);
      if (response.data && response.data.brief_id) {
        setBrief(response.data);
      }
    } catch (error) {
      console.error('Error fetching featured brief:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatWeekRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#111111] via-[#0F0F0F] to-[#0A0A0A] border border-[#C5A059]/20 rounded-lg p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-48 mb-4"></div>
        <div className="h-8 bg-white/10 rounded w-full mb-4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-white/10 rounded w-full"></div>
          <div className="h-4 bg-white/10 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!brief) {
    return null;
  }

  return (
    <div 
      className="bg-gradient-to-br from-[#111111] via-[#0F0F0F] to-[#0A0A0A] border border-[#C5A059]/20 rounded-lg overflow-hidden hover:border-[#C5A059]/40 transition-all duration-300"
      data-testid="weekly-brief-preview"
    >
      {/* Cover Image */}
      {brief.cover_image && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={brief.cover_image}
            alt={brief.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent"></div>
          <div className="absolute bottom-4 left-4">
            <div className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-3 py-1 rounded-full text-xs font-bold">
              <Star size={12} />
              Featured Analysis
            </div>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-[#C5A059] to-[#D4AF37] rounded-lg flex items-center justify-center">
            <TrendingUp className="text-black" size={20} />
          </div>
          <div>
            <h3 className="font-manrope font-bold text-white text-lg">Weekly Institutional Macro Brief</h3>
            <p className="text-gray-500 text-xs flex items-center gap-1">
              <Calendar size={12} />
              {formatWeekRange(brief.week_start_date, brief.week_end_date)}
            </p>
          </div>
        </div>

        {/* Title */}
        <h4 className="font-manrope font-bold text-xl text-white mb-3 line-clamp-2">
          {brief.title}
        </h4>

        {/* Summary */}
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
          {brief.summary}
        </p>

        {/* Highlights */}
        {brief.highlights && brief.highlights.length > 0 && (
          <div className="mb-4">
            <h5 className="text-[#C5A059] text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
              <BookOpen size={12} />
              Key Highlights
            </h5>
            <ul className="space-y-1.5">
              {brief.highlights.slice(0, 3).map((highlight, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                  <span className="text-[#C5A059] mt-0.5">•</span>
                  <span className="line-clamp-1">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Meta & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <span className="text-gray-500 text-xs flex items-center gap-1">
            <Clock size={12} />
            {brief.read_time_minutes} min read
          </span>
          <Link to={`/macro-brief/${brief.slug}`}>
            <Button 
              className="bg-[#C5A059] text-black hover:bg-[#D4AF37] text-sm"
              data-testid="read-brief-btn"
            >
              Read This Week's Brief
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WeeklyBriefPreview;
